import React, { Fragment } from 'react';
import Stepper, { StepperPage } from '../Stepper';
import Complete from '../Complete';
import ConfirmationPage from './ConfirmationPage';
import { StripeMetadataInput, SuccessData } from '../VaultForm';
import submit from './submit';
import { FinancialAccountNode, OrgNode } from '../../services/orgs/interfaces';
import { StripeEnv } from '../../util/enums';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
import { ORG_CONFIRMATION_SUBMIT_TREATMENT } from '../../split';
import logger from '../../util/logger';
import { generateDescription } from '../../util/description';

export default function OrgConfirmation({
  kyb,
  setRefreshKyb,
  primaryKyc,
  primaryKycStripePerson,
  submittedOnYourBehalf,
  financialAccountNode,
  org,
  currentUserId,
  onSuccess,
  onRevealSuccess,
  onClose,
  onRevealFail,
  statementDescriptor,
  metadata,
  showSubmitForReviewButton,
  stripeEnv,
  provider,
  token,
  raiseConfig,
  bankAccountPayableOrgId,
  fundraiserId,
  fundraiserSlug,
  showRaiseLastStep,
  shadow,
  showFaq,
  owners,
}: {
  kyb: Kyb;
  setRefreshKyb: (refresh: symbol) => void;
  primaryKyc: Kyc;
  primaryKycStripePerson?: string;
  submittedOnYourBehalf: boolean;
  financialAccountNode?: FinancialAccountNode;
  org: OrgNode;
  currentUserId: string;
  onSuccess: (res: SuccessData) => void;
  onRevealSuccess: () => void;
  onClose?: () => void;
  onRevealFail: () => void;
  statementDescriptor?: string;
  metadata?: StripeMetadataInput;
  showSubmitForReviewButton?: boolean;
  stripeEnv: StripeEnv;
  provider: VaultProvider;
  token: string;
  raiseConfig?: {
    raiseCampaignId?: string;
    raiseUserId?: string;
  };
  bankAccountPayableOrgId?: string;
  fundraiserId?: string | null;
  fundraiserSlug?: string | null;
  showRaiseLastStep?: boolean;
  shadow?: boolean;
  showFaq?: boolean;
  owners: Owner[];
}): React.JSX.Element {
  const { isReady: splitIsReady, treatments: splitTreatments } =
    useSplitTreatments({
      names: [ORG_CONFIRMATION_SUBMIT_TREATMENT.name],
      attributes: {
        orgId: org.id,
      },
    });

  const steps: StepperPage[] = [
    {
      component: (
        <Fragment>
          {token && (
            <ConfirmationPage
              kyb={kyb}
              setRefreshKyb={setRefreshKyb}
              primaryKyc={primaryKyc}
              submittedOnYourBehalf={submittedOnYourBehalf}
              onRevealSuccess={onRevealSuccess}
              onRevealFail={onRevealFail}
              token={token}
              raiseConfig={raiseConfig}
              owners={owners}
              allowPhoneNumberUpdate={showSubmitForReviewButton}
            />
          )}
        </Fragment>
      ),
      isValid: true,
      navigation: { showBack: false, showForward: true, showSteps: true },
      nextText: 'Submit for Review',
      beforeNext: async (): Promise<void> => {
        if (!splitIsReady) {
          throw new Error('Split is not ready');
        }
        const successData = await submit({
          stripeEnv,
          kyb,
          representative: primaryKyc,
          primaryKycStripePerson,
          submittedOnYourBehalf,
          financialAccountNode,
          org,
          currentUserId,
          statementDescriptor,
          metadata,
          provider,
          splitTreatments,
          bankAccountPayableOrgId,
          fundraiserId,
          fundraiserSlug,
          productDescription: generateDescription({ org }),
          owners,
        });
        logger(`OrgConfirmation successData ${JSON.stringify(successData)}`);
        onSuccess(successData);
      },
      onCancel: onClose,
      srName: 'Confirm Org KYB/KYC',
      shouldRender: true,
    },
  ];
  if (showSubmitForReviewButton && submittedOnYourBehalf) {
    steps.push({
      component: <Complete onClose={onClose} />,
      isValid: true,
      navigation: { showBack: false, showForward: false, showSteps: true },
      shouldRender: !showRaiseLastStep,
      srName: 'Confirm Org KYB/KYC Complete',
    });
  }
  return (
    <Stepper
      pages={steps}
      showSubmitForReviewButton={
        showSubmitForReviewButton && submittedOnYourBehalf
      }
      shadow={shadow}
      showFaq={showFaq}
    />
  );
}
